import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare global {
  interface Window {
    deferredPWAEvent?: BeforeInstallPromptEvent | null;
  }
}

const detectStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

export function usePwaInstall() {
  const [canPrompt, setCanPrompt] = useState(() => Boolean(window.deferredPWAEvent));
  const [isInstalled, setIsInstalled] = useState(detectStandalone);
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      window.deferredPWAEvent = e as BeforeInstallPromptEvent;
      setCanPrompt(true);
    };
    const onInstalled = () => {
      window.deferredPWAEvent = null;
      setCanPrompt(false);
      setIsInstalled(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  /** Geeft true als het native venster is getoond; false = toon de handmatige uitleg. */
  const install = async (): Promise<boolean> => {
    const ev = window.deferredPWAEvent;
    if (!ev) return false;
    await ev.prompt();
    const { outcome } = await ev.userChoice;
    window.deferredPWAEvent = null;
    setCanPrompt(false);
    if (outcome === 'accepted') setIsInstalled(true);
    return true;
  };

  return { canPrompt, isInstalled, isIOS, install };
}
